/**
 * EChem Quote System — API Client + UI Helpers
 * Vanilla JS, no build step, no imports.
 */
(function () {
  "use strict";

  /* ================================================================
   * API Client
   * ================================================================ */
  var quoteApi = {
    _baseUrl: function () {
      return (window.__quoteApiUrl || "").replace(/\/+$/, "");
    },

    _headers: function () {
      var h = { "Content-Type": "application/json" };
      if (window.__quoteAuth) {
        h["X-Customer-Token"] = window.__quoteAuth.data;
        h["X-Customer-Sig"] = window.__quoteAuth.sig;
      }
      return h;
    },

    _request: function (method, path, body) {
      var url = this._baseUrl() + path;
      var opts = { method: method, headers: this._headers() };
      if (body) opts.body = JSON.stringify(body);

      // Wrap fetch with a 30-second timeout
      var controller =
        typeof AbortController !== "undefined" ? new AbortController() : null;
      if (controller) opts.signal = controller.signal;
      var timeoutId = controller
        ? setTimeout(function () {
            controller.abort();
          }, 30000)
        : null;

      return fetch(url, opts)
        .then(function (res) {
          if (timeoutId) clearTimeout(timeoutId);
          if (res.status === 401) {
            cusQuoteUI.showToast(
              "Session expired. Please refresh the page.",
              "error",
            );
            return Promise.reject(new Error("Unauthorized"));
          }
          return res.json().then(function (data) {
            if (!res.ok) {
              // Show user-friendly toast based on status code
              var msg =
                res.status >= 500
                  ? "Something went wrong. Please try again."
                  : (data && data.error) || "Request failed.";
              cusQuoteUI.showToast(msg, "error");
              return Promise.reject(data);
            }
            return data;
          });
        })
        .catch(function (err) {
          if (timeoutId) clearTimeout(timeoutId);
          // Skip toast for errors already handled above (401, API errors with code)
          if ((err && err.message === "Unauthorized") || (err && err.code)) {
            return Promise.reject(err);
          }
          var msg =
            err && err.name === "AbortError"
              ? "Request timed out. Please try again."
              : "Connection error. Please check your internet.";
          cusQuoteUI.showToast(msg, "error");
          return Promise.reject(err);
        });
    },

    getCart: function () {
      return this._request("GET", "/api/cart");
    },

    addItem: function (product) {
      return this._request("POST", "/api/cart/add", product);
    },

    updateItem: function (variantId, data) {
      data.variant_id = variantId;
      return this._request("PUT", "/api/cart/update", data);
    },

    removeItem: function (variantId) {
      return this._request("DELETE", "/api/cart/remove", {
        variant_id: variantId,
      });
    },

    submitQuote: function (quoteData) {
      return this._request("POST", "/api/quote/submit", quoteData);
    },

    getHistory: function () {
      return this._request("GET", "/api/quote/history");
    },
  };

  /* ================================================================
   * UI Helpers
   * ================================================================ */
  var cusQuoteUI = {
    /**
     * Update the quote cart badge count in the header.
     */
    updateCartBadge: function (count) {
      var badges = document.querySelectorAll(".cus-quote-badge");
      badges.forEach(function (badge) {
        if (count > 0) {
          badge.textContent = count > 99 ? "99+" : count;
          badge.removeAttribute("hidden");
        } else {
          badge.setAttribute("hidden", "");
        }
      });
    },

    /**
     * Show a toast notification.
     */
    showToast: function (message, type) {
      type = type || "success";
      var existing = document.querySelector(".cus-quote-toast");
      if (existing) existing.remove();

      var toast = document.createElement("div");
      toast.className = "cus-quote-toast cus-quote-toast--" + type;
      toast.setAttribute("role", "alert");
      toast.textContent = message;
      document.body.appendChild(toast);

      // Trigger reflow for animation
      toast.offsetHeight;
      toast.classList.add("cus-quote-toast--visible");

      setTimeout(function () {
        toast.classList.remove("cus-quote-toast--visible");
        setTimeout(function () {
          toast.remove();
        }, 300);
      }, 3500);
    },

    /**
     * Show a modal dialog with custom content.
     * Returns { dialog, close } — call close() to dismiss.
     */
    showModal: function (title, bodyHtml, buttons) {
      var overlay = document.createElement("div");
      overlay.className = "cus-quote-modal-overlay";

      var modal = document.createElement("div");
      modal.className = "cus-quote-modal";
      modal.setAttribute("role", "dialog");
      modal.setAttribute("aria-label", title);

      var header = document.createElement("div");
      header.className = "cus-quote-modal__header";
      header.innerHTML =
        '<h3 class="cus-quote-modal__title">' +
        cusQuoteUI.escapeHtml(title) +
        "</h3>";

      var closeBtn = document.createElement("button");
      closeBtn.type = "button";
      closeBtn.className = "cus-quote-modal__close";
      closeBtn.setAttribute("aria-label", "Close");
      closeBtn.innerHTML =
        '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="4" x2="16" y2="16"/><line x1="16" y1="4" x2="4" y2="16"/></svg>';
      header.appendChild(closeBtn);

      var body = document.createElement("div");
      body.className = "cus-quote-modal__body";
      body.innerHTML = bodyHtml;

      modal.appendChild(header);
      modal.appendChild(body);

      if (buttons && buttons.length) {
        var footer = document.createElement("div");
        footer.className = "cus-quote-modal__footer";
        buttons.forEach(function (btn) {
          var el = document.createElement("button");
          el.type = "button";
          el.className = "button " + (btn.className || "");
          el.textContent = btn.label;
          if (btn.onClick) el.addEventListener("click", btn.onClick);
          footer.appendChild(el);
        });
        modal.appendChild(footer);
      }

      overlay.appendChild(modal);
      document.body.appendChild(overlay);

      // Trigger animation
      requestAnimationFrame(function () {
        overlay.classList.add("cus-quote-modal-overlay--visible");
      });

      function close() {
        overlay.classList.remove("cus-quote-modal-overlay--visible");
        setTimeout(function () {
          overlay.remove();
        }, 300);
      }

      closeBtn.addEventListener("click", close);
      overlay.addEventListener("click", function (e) {
        if (e.target === overlay) close();
      });

      return { overlay: overlay, modal: modal, body: body, close: close };
    },

    escapeHtml: function (str) {
      var div = document.createElement("div");
      div.textContent = str;
      return div.innerHTML;
    },

    /**
     * Format price as currency string.
     */
    formatMoney: function (cents) {
      return "$" + (cents / 100).toFixed(2);
    },
  };

  /* ================================================================
   * Init: Load cart badge count on page load
   * ================================================================ */
  function initQuoteBadge() {
    if (!window.__quoteAuth) return;
    quoteApi
      .getCart()
      .then(function (data) {
        cusQuoteUI.updateCartBadge((data.data && data.data.count) || 0);
      })
      .catch(function () {
        // Silently fail — badge stays hidden
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initQuoteBadge);
  } else {
    initQuoteBadge();
  }

  /* ================================================================
   * Expose globals
   * ================================================================ */
  window.quoteApi = quoteApi;
  window.cusQuoteUI = cusQuoteUI;

  /* ================================================================
   * Add to Quote — Click Handler (Event Delegation)
   * Works regardless of when buttons appear in the DOM.
   * ================================================================ */
  document.addEventListener("click", function (e) {
    var btn = e.target.closest(".cus-atq-btn:not(.cus-atq-btn--login)");
    if (!btn || btn.classList.contains("cus-atq-btn--loading")) return;

    // Auth guard — redirect to login if not authenticated
    if (!window.__quoteAuth) {
      window.location.href = "/account/login";
      return;
    }

    var i18n = window.__quoteI18n || {};
    var originalHtml = btn.innerHTML;
    btn.classList.add("cus-atq-btn--loading");
    btn.textContent = i18n.adding || "Adding...";

    var data = {
      product_id: btn.dataset.productId,
      variant_id: btn.dataset.variantId,
      title: btn.dataset.productTitle,
      variant_title: btn.dataset.variantTitle,
      sku: btn.dataset.variantSku,
      price: parseInt(btn.dataset.variantPrice, 10) || 0,
      quantity: 1,
      image: btn.dataset.productImage || "",
      inventory: parseInt(btn.dataset.variantInventory, 10) || 0,
      hidePrice: btn.dataset.hidePrice === "true",
      handle: (btn.dataset.productUrl || "").replace(/^\/products\//, ""),
    };

    // Read current variant from form hidden input (most accurate after variant switch)
    var form = btn.closest('form[data-type="add-to-cart-form"]');
    if (form) {
      var variantInput = form.querySelector('input[ref="variantId"]');
      if (variantInput && variantInput.value) {
        data.variant_id = variantInput.value;
      }
    }

    quoteApi
      .addItem(data)
      .then(function (res) {
        btn.classList.remove("cus-atq-btn--loading");
        btn.innerHTML = originalHtml;

        if (res.data) {
          cusQuoteUI.updateCartBadge(res.data.count || 0);
        }

        var quoteIconUrl =
          "https://cdn.shopify.com/s/files/1/0774/6591/1526/files/echem-quote-icon.png";
        var bodyHtml =
          "<p>" +
          cusQuoteUI.escapeHtml(data.title) +
          " " +
          (i18n.added_message || "has been added to your quote cart.") +
          "</p>" +
          "<p style='margin-top:12px;color:#555;font-size:14px;line-height:24px;'>" +
          "You can find your Quote Cart by clicking the " +
          "<img src='" +
          quoteIconUrl +
          "' alt='Quote Cart' style='width:18px;height:18px;vertical-align:middle;display:inline;margin:0 2px;'>" +
          " icon at the top-right of the navigation bar.</p>";
        var m = cusQuoteUI.showModal(
          i18n.added_title || "Item Added to Quote!",
          bodyHtml,
          [
            {
              label: i18n.keep_shopping || "Keep Shopping",
              className: "button-secondary",
              onClick: function () {
                m.close();
              },
            },
            {
              label: i18n.go_to_cart || "Go to Quote Cart",
              className: "",
              onClick: function () {
                window.location.href = "/pages/quote-cart";
              },
            },
          ],
        );
      })
      .catch(function (err) {
        btn.classList.remove("cus-atq-btn--loading");
        btn.innerHTML = originalHtml;
        cusQuoteUI.showToast(
          (err && err.message) ||
            i18n.error_generic ||
            "Failed to add item. Please try again.",
          "error",
        );
        console.error("[Add to Quote]", err);
      });
  });

  /* ── Variant switching — keep button data in sync ── */
  document
    .querySelectorAll('input[ref="variantId"]')
    .forEach(function (variantInput) {
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          if (
            mutation.type !== "attributes" ||
            mutation.attributeName !== "value"
          )
            return;
          var newVariantId = variantInput.value;
          var container =
            variantInput.closest("product-form-component") ||
            variantInput.closest("form");
          if (!container) return;
          var btns = container.querySelectorAll(
            ".cus-atq-btn:not(.cus-atq-btn--login)",
          );
          btns.forEach(function (btn) {
            btn.dataset.variantId = newVariantId;
            var productUrl = btn.dataset.productUrl;
            if (!productUrl) return;
            fetch(productUrl + ".js")
              .then(function (r) {
                return r.json();
              })
              .then(function (pData) {
                var v = pData.variants.find(function (variant) {
                  return String(variant.id) === String(newVariantId);
                });
                if (v) {
                  btn.dataset.variantTitle = v.title || "";
                  btn.dataset.variantSku = v.sku || "";
                  btn.dataset.variantPrice = String(v.price || 0);
                  btn.dataset.variantInventory = String(
                    v.inventory_quantity || 0,
                  );
                }
              })
              .catch(function () {});
          });
        });
      });
      observer.observe(variantInput, { attributes: true });
    });

  /* ================================================================
   * Quote Cart Page — Loading, Rendering, and Management
   * Only activates when #cus-quote-cart is present in the DOM.
   * Replaces the inline script to avoid section-rendering issues.
   * ================================================================ */
  var _qcEl = document.getElementById("cus-quote-cart");
  if (_qcEl) {
    var _qcLoading = document.getElementById("cus-qc-loading");
    var _qcEmpty = document.getElementById("cus-qc-empty");
    var _qcContent = document.getElementById("cus-qc-content");
    var _qcTbody = document.getElementById("cus-qc-tbody");
    var _qcNextBtn = document.getElementById("cus-qc-next-btn");
    var _qcItems = [];

    function _qcEsc(str) {
      var d = document.createElement("div");
      d.textContent = str || "";
      return d.innerHTML;
    }

    function _qcRender(items) {
      _qcItems = items || [];
      if (_qcLoading) _qcLoading.style.display = "none";

      if (_qcItems.length === 0) {
        if (_qcEmpty) _qcEmpty.style.display = "";
        if (_qcContent) _qcContent.style.display = "none";
        if (_qcNextBtn) {
          _qcNextBtn.disabled = true;
          _qcNextBtn.classList.add("cus-quote-cart__next-btn--disabled");
        }
        return;
      }

      if (_qcEmpty) _qcEmpty.style.display = "none";
      if (_qcContent) _qcContent.style.display = "";
      if (_qcNextBtn) {
        _qcNextBtn.disabled = false;
        _qcNextBtn.classList.remove("cus-quote-cart__next-btn--disabled");
      }

      var i18n = window.__quoteI18n || {};
      var html = "";
      _qcItems.forEach(function (item, idx) {
        var imgHtml = item.image
          ? '<img src="' +
            _qcEsc(item.image) +
            '" class="cus-quote-cart__item-image" width="64" height="64" loading="lazy" alt="">'
          : "";

        html +=
          '<tr data-variant-id="' +
          _qcEsc(item.variantId || item.variant_id) +
          '">' +
          "<td>" +
          '<div class="cus-quote-cart__item-info">' +
          imgHtml +
          "<div>" +
          '<a href="/products/' +
          _qcEsc(item.handle || "") +
          '" class="cus-quote-cart__item-title">' +
          _qcEsc(item.title) +
          "</a>" +
          "</div>" +
          "</div>" +
          "</td>" +
          "<td>" +
          (function () {
            var vt = item.variantTitle || item.variant_title || "";
            return vt === "Default Title" ? "" : _qcEsc(vt);
          })() +
          "</td>" +
          '<td class="cus-quote-cart__item-sku">' +
          _qcEsc(item.sku) +
          "</td>" +
          "<td>" +
          '<input type="number" class="cus-quote-cart__qty-input" data-index="' +
          idx +
          '" value="' +
          (item.quantity || 1) +
          '" min="1">' +
          "</td>" +
          "<td>" +
          '<button type="button" class="cus-quote-cart__delete-btn" data-index="' +
          idx +
          '" aria-label="' +
          (i18n.delete_label || "Delete") +
          '">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>' +
          "</button>" +
          "</td>" +
          "</tr>";
      });

      if (_qcTbody) _qcTbody.innerHTML = html;
    }

    // Load cart data
    if (!window.__quoteAuth) {
      if (_qcLoading) _qcLoading.style.display = "none";
      if (_qcEmpty) _qcEmpty.style.display = "";
    } else {
      quoteApi
        .getCart()
        .then(function (data) {
          _qcRender((data.data && data.data.items) || []);
        })
        .catch(function () {
          if (_qcLoading) _qcLoading.style.display = "none";
          if (_qcEmpty) _qcEmpty.style.display = "";
        });
    }

    // Quantity change
    document.addEventListener("change", function (e) {
      if (!e.target.matches(".cus-quote-cart__qty-input")) return;
      var idx = parseInt(e.target.dataset.index, 10);
      var item = _qcItems[idx];
      if (!item) return;
      var newQty = parseInt(e.target.value, 10);
      if (isNaN(newQty) || newQty < 1) {
        e.target.value = 1;
        newQty = 1;
      }
      if (newQty > 99999) {
        e.target.value = 99999;
        newQty = 99999;
      }
      item.quantity = newQty;
      var vid = item.variantId || item.variant_id;
      quoteApi.updateItem(vid, { quantity: newQty }).catch(function () {
        cusQuoteUI.showToast("Failed to update quantity.", "error");
      });
    });

    // Delete item
    document.addEventListener("click", function (e) {
      var deleteBtn = e.target.closest(".cus-quote-cart__delete-btn");
      if (!deleteBtn) return;
      var idx = parseInt(deleteBtn.dataset.index, 10);
      var item = _qcItems[idx];
      if (!item) return;
      var vid = item.variantId || item.variant_id;
      quoteApi
        .removeItem(vid)
        .then(function (data) {
          var cartData = data.data || {};
          _qcRender(cartData.items || []);
          cusQuoteUI.updateCartBadge(cartData.count || 0);
        })
        .catch(function () {
          cusQuoteUI.showToast("Failed to remove item.", "error");
        });
    });

    // Next Step — Purpose of Purchase modal
    if (_qcNextBtn) {
      _qcNextBtn.addEventListener("click", function () {
        if (_qcItems.length === 0) return;

        var i18n = window.__quoteI18n || {};
        var bodyHtml =
          '<div class="cus-quote-purpose__options">' +
          '<label class="cus-quote-purpose__option">' +
          '<input type="radio" name="cus-purpose" value="For Own Use" checked>' +
          (i18n.for_own_use || "For Own Use") +
          "</label>" +
          '<label class="cus-quote-purpose__option">' +
          '<input type="radio" name="cus-purpose" value="Resale">' +
          (i18n.for_resale || "Resale") +
          "</label>" +
          "</div>" +
          '<div class="cus-quote-purpose__enduser" id="cus-purpose-enduser">' +
          '<label class="cus-quote-form__label" for="cus-enduser-input">' +
          (i18n.end_user_name || "End User Name") +
          "</label>" +
          '<input type="text" class="cus-quote-form__input" id="cus-enduser-input" placeholder="End user name">' +
          "</div>";

        var m = cusQuoteUI.showModal(
          i18n.purpose_title || "Purpose of Purchase",
          bodyHtml,
          [
            {
              label: i18n.cancel || "Cancel",
              className: "button-secondary",
              onClick: function () {
                m.close();
              },
            },
            {
              label: i18n.confirm || "Confirm",
              className: "",
              onClick: function () {
                var selected = m.body.querySelector(
                  'input[name="cus-purpose"]:checked',
                );
                var purpose = selected ? selected.value : "For Own Use";
                var endUser = "";
                if (purpose === "Resale") {
                  endUser =
                    (m.body.querySelector("#cus-enduser-input") || {}).value ||
                    "";
                }
                try {
                  sessionStorage.setItem(
                    "cus_quote_items",
                    JSON.stringify(_qcItems),
                  );
                  sessionStorage.setItem("cus_quote_purpose", purpose);
                  sessionStorage.setItem("cus_quote_enduser", endUser);
                } catch (ex) {}
                m.close();
                window.location.href = "/pages/quote-form";
              },
            },
          ],
        );

        m.body.addEventListener("change", function (e) {
          if (e.target.name !== "cus-purpose") return;
          var endUserDiv = m.body.querySelector("#cus-purpose-enduser");
          if (endUserDiv) {
            endUserDiv.classList.toggle(
              "cus-quote-purpose__enduser--visible",
              e.target.value === "Resale",
            );
          }
        });
      });
    }
  }
  /* ================================================================
   * Quote Form Page — Country/State, Auto-fill, Validation, Submit
   * Only activates when #cus-quote-form is present in the DOM.
   * Replaces the inline script to avoid section-rendering issues.
   * ================================================================ */
  var _qfEl = document.getElementById("cus-quote-form");
  if (_qfEl) {
    // Country/State data (minimal set — top countries for B2B)
    var _qfCountryData = {
      US: {
        name: "United States",
        states: [
          "Alabama",
          "Alaska",
          "Arizona",
          "Arkansas",
          "California",
          "Colorado",
          "Connecticut",
          "Delaware",
          "Florida",
          "Georgia",
          "Hawaii",
          "Idaho",
          "Illinois",
          "Indiana",
          "Iowa",
          "Kansas",
          "Kentucky",
          "Louisiana",
          "Maine",
          "Maryland",
          "Massachusetts",
          "Michigan",
          "Minnesota",
          "Mississippi",
          "Missouri",
          "Montana",
          "Nebraska",
          "Nevada",
          "New Hampshire",
          "New Jersey",
          "New Mexico",
          "New York",
          "North Carolina",
          "North Dakota",
          "Ohio",
          "Oklahoma",
          "Oregon",
          "Pennsylvania",
          "Rhode Island",
          "South Carolina",
          "South Dakota",
          "Tennessee",
          "Texas",
          "Utah",
          "Vermont",
          "Virginia",
          "Washington",
          "West Virginia",
          "Wisconsin",
          "Wyoming",
        ],
      },
      CA: {
        name: "Canada",
        states: [
          "Alberta",
          "British Columbia",
          "Manitoba",
          "New Brunswick",
          "Newfoundland and Labrador",
          "Nova Scotia",
          "Ontario",
          "Prince Edward Island",
          "Quebec",
          "Saskatchewan",
          "Northwest Territories",
          "Nunavut",
          "Yukon",
        ],
      },
      GB: {
        name: "United Kingdom",
        states: ["England", "Scotland", "Wales", "Northern Ireland"],
      },
      AU: {
        name: "Australia",
        states: [
          "New South Wales",
          "Victoria",
          "Queensland",
          "South Australia",
          "Western Australia",
          "Tasmania",
          "Northern Territory",
          "Australian Capital Territory",
        ],
      },
      DE: {
        name: "Germany",
        states: [
          "Baden-Württemberg",
          "Bavaria",
          "Berlin",
          "Brandenburg",
          "Bremen",
          "Hamburg",
          "Hesse",
          "Lower Saxony",
          "Mecklenburg-Vorpommern",
          "North Rhine-Westphalia",
          "Rhineland-Palatinate",
          "Saarland",
          "Saxony",
          "Saxony-Anhalt",
          "Schleswig-Holstein",
          "Thuringia",
        ],
      },
      FR: { name: "France", states: [] },
      JP: { name: "Japan", states: [] },
      CN: {
        name: "China",
        states: [
          "Beijing",
          "Shanghai",
          "Guangdong",
          "Zhejiang",
          "Jiangsu",
          "Shandong",
          "Henan",
          "Sichuan",
          "Hubei",
          "Hunan",
          "Fujian",
          "Anhui",
          "Hebei",
          "Liaoning",
          "Shaanxi",
          "Jiangxi",
          "Chongqing",
          "Yunnan",
          "Guangxi",
          "Shanxi",
          "Guizhou",
          "Heilongjiang",
          "Jilin",
          "Inner Mongolia",
          "Gansu",
          "Xinjiang",
          "Hainan",
          "Tianjin",
          "Ningxia",
          "Qinghai",
          "Tibet",
        ],
      },
      KR: { name: "South Korea", states: [] },
      IN: {
        name: "India",
        states: [
          "Andhra Pradesh",
          "Arunachal Pradesh",
          "Assam",
          "Bihar",
          "Chhattisgarh",
          "Goa",
          "Gujarat",
          "Haryana",
          "Himachal Pradesh",
          "Jharkhand",
          "Karnataka",
          "Kerala",
          "Madhya Pradesh",
          "Maharashtra",
          "Manipur",
          "Meghalaya",
          "Mizoram",
          "Nagaland",
          "Odisha",
          "Punjab",
          "Rajasthan",
          "Sikkim",
          "Tamil Nadu",
          "Telangana",
          "Tripura",
          "Uttar Pradesh",
          "Uttarakhand",
          "West Bengal",
        ],
      },
      SG: { name: "Singapore", states: [] },
      NZ: { name: "New Zealand", states: [] },
      MX: { name: "Mexico", states: [] },
      BR: { name: "Brazil", states: [] },
      IT: { name: "Italy", states: [] },
      ES: { name: "Spain", states: [] },
      NL: { name: "Netherlands", states: [] },
      SE: { name: "Sweden", states: [] },
      CH: { name: "Switzerland", states: [] },
      AT: { name: "Austria", states: [] },
      BE: { name: "Belgium", states: [] },
      DK: { name: "Denmark", states: [] },
      FI: { name: "Finland", states: [] },
      NO: { name: "Norway", states: [] },
      IE: { name: "Ireland", states: [] },
      PL: { name: "Poland", states: [] },
      CZ: { name: "Czech Republic", states: [] },
      IL: { name: "Israel", states: [] },
      TW: { name: "Taiwan", states: [] },
      TH: { name: "Thailand", states: [] },
      MY: { name: "Malaysia", states: [] },
      PH: { name: "Philippines", states: [] },
      ID: { name: "Indonesia", states: [] },
      VN: { name: "Vietnam", states: [] },
      AE: { name: "United Arab Emirates", states: [] },
      SA: { name: "Saudi Arabia", states: [] },
    };

    function _qfPopulateCountries(selectEl) {
      var keys = Object.keys(_qfCountryData);
      keys.sort(function (a, b) {
        return _qfCountryData[a].name.localeCompare(_qfCountryData[b].name);
      });
      keys.forEach(function (code) {
        var opt = document.createElement("option");
        opt.value = code;
        opt.textContent = _qfCountryData[code].name;
        selectEl.appendChild(opt);
      });
    }

    function _qfPopulateStates(stateSelect, countryCode) {
      stateSelect.innerHTML = '<option value="">-- Select --</option>';
      var c = _qfCountryData[countryCode];
      if (c && c.states && c.states.length > 0) {
        c.states.forEach(function (s) {
          var opt = document.createElement("option");
          opt.value = s;
          opt.textContent = s;
          stateSelect.appendChild(opt);
        });
        stateSelect.closest(".cus-quote-form__field").style.display = "";
      } else if (c) {
        var textInput = document.createElement("input");
        textInput.type = "text";
        textInput.className = "cus-quote-form__input";
        textInput.id = stateSelect.id;
        textInput.placeholder = "State / Province";
        stateSelect.replaceWith(textInput);
      }
    }

    var _qfShipCountry = document.getElementById("cus-qf-ship-country");
    var _qfShipState = document.getElementById("cus-qf-ship-state");
    var _qfBillCountry = document.getElementById("cus-qf-bill-country");
    var _qfBillState = document.getElementById("cus-qf-bill-state");

    if (_qfShipCountry) _qfPopulateCountries(_qfShipCountry);
    if (_qfBillCountry) _qfPopulateCountries(_qfBillCountry);

    if (_qfShipCountry) {
      _qfShipCountry.addEventListener("change", function () {
        _qfPopulateStates(_qfShipState, _qfShipCountry.value);
      });
    }
    if (_qfBillCountry) {
      _qfBillCountry.addEventListener("change", function () {
        _qfPopulateStates(_qfBillState, _qfBillCountry.value);
      });
    }

    // Toggle billing address
    var _qfSameAddr = document.getElementById("cus-qf-same-addr");
    var _qfBillFields = document.getElementById("cus-qf-bill-fields");
    if (_qfSameAddr) {
      _qfSameAddr.addEventListener("change", function () {
        if (_qfBillFields)
          _qfBillFields.style.display = _qfSameAddr.checked ? "none" : "";
      });
    }

    function _qfSetVal(id, val) {
      var el = document.getElementById(id);
      if (el && !el.value) el.value = val;
    }

    // Auto-fill from quoteAuth
    function _qfAutoFill() {
      if (!window.__quoteAuth) return;
      var a = window.__quoteAuth;
      _qfSetVal("cus-qf-name", a.customerName || "");
      _qfSetVal("cus-qf-email", a.customerEmail || "");
      _qfSetVal("cus-qf-phone", a.customerPhone || "");
      _qfSetVal("cus-qf-company", a.customerCompany || "");
    }

    // Restore from sessionStorage if returning from review
    function _qfRestoreFromSession() {
      try {
        var stored = sessionStorage.getItem("cus_quote_customer");
        if (!stored) return;
        var d = JSON.parse(stored);
        _qfSetVal("cus-qf-name", d.name);
        _qfSetVal("cus-qf-email", d.email);
        _qfSetVal("cus-qf-phone", d.phone);
        _qfSetVal("cus-qf-company", d.company);
        _qfSetVal("cus-qf-ship-addr1", d.shippingAddress.address1);
        _qfSetVal("cus-qf-ship-addr2", d.shippingAddress.address2);
        if (d.shippingAddress.country && _qfShipCountry) {
          _qfShipCountry.value = d.shippingAddress.country;
          _qfShipCountry.dispatchEvent(new Event("change"));
          setTimeout(function () {
            var s = document.getElementById("cus-qf-ship-state");
            if (s) s.value = d.shippingAddress.state || "";
          }, 50);
        }
        _qfSetVal("cus-qf-ship-city", d.shippingAddress.city);
        _qfSetVal("cus-qf-ship-zip", d.shippingAddress.zip);

        if (d.billSameAsShip === false) {
          if (_qfSameAddr) _qfSameAddr.checked = false;
          if (_qfBillFields) _qfBillFields.style.display = "";
          _qfSetVal("cus-qf-bill-addr1", d.billAddress.address1);
          _qfSetVal("cus-qf-bill-addr2", d.billAddress.address2);
          if (d.billAddress.country && _qfBillCountry) {
            _qfBillCountry.value = d.billAddress.country;
            _qfBillCountry.dispatchEvent(new Event("change"));
            setTimeout(function () {
              var s = document.getElementById("cus-qf-bill-state");
              if (s) s.value = d.billAddress.state || "";
            }, 50);
          }
          _qfSetVal("cus-qf-bill-city", d.billAddress.city);
          _qfSetVal("cus-qf-bill-zip", d.billAddress.zip);
        }

        _qfSetVal("cus-qf-attn1", (d.attn && d.attn[0]) || "");
        _qfSetVal("cus-qf-attn2", (d.attn && d.attn[1]) || "");
        _qfSetVal("cus-qf-attn3", (d.attn && d.attn[2]) || "");
        _qfSetVal("cus-qf-remark", d.remark || "");
      } catch (e) {}
    }

    // Validation
    function _qfValidateForm() {
      var valid = true;
      var requiredIds = [
        "cus-qf-name",
        "cus-qf-email",
        "cus-qf-phone",
        "cus-qf-company",
        "cus-qf-ship-addr1",
        "cus-qf-ship-country",
        "cus-qf-ship-state",
        "cus-qf-ship-city",
        "cus-qf-ship-zip",
      ];

      requiredIds.forEach(function (id) {
        var el = document.getElementById(id);
        if (!el) return;
        var err = el.parentElement.querySelector(".cus-quote-form__error");
        if (!el.value.trim()) {
          el.classList.add("cus-quote-form__input--error");
          if (err) err.classList.add("cus-quote-form__error--visible");
          valid = false;
        } else {
          el.classList.remove("cus-quote-form__input--error");
          if (err) err.classList.remove("cus-quote-form__error--visible");
        }
      });

      // Email format
      var emailEl = document.getElementById("cus-qf-email");
      if (
        emailEl &&
        emailEl.value &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailEl.value)
      ) {
        emailEl.classList.add("cus-quote-form__input--error");
        var emailErr = emailEl.parentElement.querySelector(
          ".cus-quote-form__error",
        );
        if (emailErr) emailErr.classList.add("cus-quote-form__error--visible");
        valid = false;
      }

      return valid;
    }

    function _qfGetVal(id) {
      var el = document.getElementById(id);
      return el ? el.value.trim() : "";
    }

    // Form submit
    var _qfForm = document.getElementById("cus-qf-form");
    if (_qfForm) {
      _qfForm.addEventListener("submit", function (e) {
        e.preventDefault();
        if (!_qfValidateForm()) {
          var firstErr = _qfForm.querySelector(".cus-quote-form__input--error");
          if (firstErr) firstErr.focus();
          return;
        }

        var shipStateEl = document.getElementById("cus-qf-ship-state");
        var billStateEl = document.getElementById("cus-qf-bill-state");

        var data = {
          name: _qfGetVal("cus-qf-name"),
          email: _qfGetVal("cus-qf-email"),
          phone: _qfGetVal("cus-qf-phone"),
          company: _qfGetVal("cus-qf-company"),
          shippingAddress: {
            address1: _qfGetVal("cus-qf-ship-addr1"),
            address2: _qfGetVal("cus-qf-ship-addr2"),
            country: _qfGetVal("cus-qf-ship-country"),
            state: shipStateEl ? shipStateEl.value : "",
            city: _qfGetVal("cus-qf-ship-city"),
            zip: _qfGetVal("cus-qf-ship-zip"),
          },
          billSameAsShip: _qfSameAddr ? _qfSameAddr.checked : true,
          billAddress:
            _qfSameAddr && _qfSameAddr.checked
              ? {}
              : {
                  address1: _qfGetVal("cus-qf-bill-addr1"),
                  address2: _qfGetVal("cus-qf-bill-addr2"),
                  country: _qfGetVal("cus-qf-bill-country"),
                  state: billStateEl ? billStateEl.value : "",
                  city: _qfGetVal("cus-qf-bill-city"),
                  zip: _qfGetVal("cus-qf-bill-zip"),
                },
          attn: [
            _qfGetVal("cus-qf-attn1"),
            _qfGetVal("cus-qf-attn2"),
            _qfGetVal("cus-qf-attn3"),
          ].filter(Boolean),
          remark: _qfGetVal("cus-qf-remark"),
        };

        try {
          sessionStorage.setItem("cus_quote_customer", JSON.stringify(data));
        } catch (e) {}

        window.location.href = "/pages/quote-review";
      });
    }

    // Init
    _qfRestoreFromSession();
    _qfAutoFill();
  }

  /* ================================================================
   * Quote Review Page — Render review data & submit
   * Only activates when #cus-quote-review is present in the DOM.
   * Replaces the inline script to avoid section-rendering issues.
   * ================================================================ */
  var _qrEl = document.getElementById("cus-quote-review");
  if (_qrEl) {
    var _qrContentEl = document.getElementById("cus-qr-content");
    var _qrSuccessEl = document.getElementById("cus-qr-success");
    var _qrQuoteIdEl = document.getElementById("cus-qr-quote-id");
    var _qrI18n = window.__quoteI18n || {};

    function _qrEsc(str) {
      var d = document.createElement("div");
      d.textContent = str || "";
      return d.innerHTML;
    }

    function _qrGetSession(key) {
      try {
        return JSON.parse(sessionStorage.getItem(key));
      } catch (e) {
        return null;
      }
    }

    var _qrItems = _qrGetSession("cus_quote_items") || [];
    var _qrCustomer = _qrGetSession("cus_quote_customer") || {};
    var _qrPurpose =
      sessionStorage.getItem("cus_quote_purpose") || "For Own Use";
    var _qrEndUser = sessionStorage.getItem("cus_quote_enduser") || "";

    // Redirect if no data
    if (_qrItems.length === 0) {
      window.location.href = "/pages/quote-cart";
    } else {
      var _qrCountryNames = {
        US: "United States",
        CA: "Canada",
        GB: "United Kingdom",
        AU: "Australia",
        DE: "Germany",
        FR: "France",
        JP: "Japan",
        CN: "China",
        KR: "South Korea",
        IN: "India",
        SG: "Singapore",
        NZ: "New Zealand",
        MX: "Mexico",
        BR: "Brazil",
        IT: "Italy",
        ES: "Spain",
        NL: "Netherlands",
        SE: "Sweden",
        CH: "Switzerland",
        AT: "Austria",
        BE: "Belgium",
        DK: "Denmark",
        FI: "Finland",
        NO: "Norway",
        IE: "Ireland",
        PL: "Poland",
        CZ: "Czech Republic",
        IL: "Israel",
        TW: "Taiwan",
        TH: "Thailand",
        MY: "Malaysia",
        PH: "Philippines",
        ID: "Indonesia",
        VN: "Vietnam",
        AE: "United Arab Emirates",
        SA: "Saudi Arabia",
      };

      function _qrCountryName(code) {
        return _qrCountryNames[code] || code || "";
      }

      function _qrFormatAddr(addr) {
        if (!addr || !addr.address1) return "N/A";
        var parts = [addr.address1];
        if (addr.address2) parts.push(addr.address2);
        parts.push(
          [addr.city, addr.state, addr.zip].filter(Boolean).join(", "),
        );
        parts.push(_qrCountryName(addr.country));
        return parts.filter(Boolean).join("<br>");
      }

      function _qrRender() {
        var html = "";
        var i = _qrI18n;

        // Products
        html += '<div class="cus-quote-review__section">';
        html += '<div class="cus-quote-review__section-header">';
        html +=
          '<h3 class="cus-quote-review__section-title">' +
          (i.rv_products || "Products") +
          " (" +
          _qrItems.length +
          ")</h3>";
        html +=
          '<a href="/pages/quote-cart" class="cus-quote-review__edit-link">' +
          (i.rv_edit || "Edit") +
          "</a>";
        html +=
          '</div><div class="cus-quote-review__section-body"><div class="cus-quote-review__table-wrapper">';
        html += '<table class="cus-quote-cart__table"><thead><tr>';
        html += "<th>" + (i.rv_product || "Product") + "</th>";
        html += "<th>" + (i.rv_variant || "Variant") + "</th>";
        html += "<th>" + (i.rv_sku || "SKU") + "</th>";
        html += "<th>" + (i.rv_qty || "Qty") + "</th>";
        html += "</tr></thead><tbody>";
        _qrItems.forEach(function (item) {
          var imgHtml = item.image
            ? '<img src="' +
              _qrEsc(item.image) +
              '" width="48" height="48" style="width:48px;height:48px;max-width:48px;object-fit:cover;border-radius:4px;" loading="lazy" alt="">'
            : "";
          html +=
            '<tr><td><div class="cus-quote-cart__item-info">' +
            imgHtml +
            '<div><span class="cus-quote-cart__item-title">' +
            _qrEsc(item.title) +
            "</span></div></div></td>";
          html +=
            "<td>" +
            (function () {
              var vt = item.variantTitle || item.variant_title || "";
              return vt === "Default Title" ? "" : _qrEsc(vt);
            })() +
            "</td>";
          html += "<td>" + _qrEsc(item.sku) + "</td>";
          html += "<td>" + (item.quantity || 1) + "</td></tr>";
        });
        html += "</tbody></table></div></div></div>";

        // Purpose of Purchase
        html += '<div class="cus-quote-review__section">';
        html += '<div class="cus-quote-review__section-header">';
        html +=
          '<h3 class="cus-quote-review__section-title">' +
          (i.rv_purpose || "Purpose of Purchase") +
          "</h3>";
        html +=
          '<a href="/pages/quote-cart" class="cus-quote-review__edit-link">' +
          (i.rv_edit || "Edit") +
          "</a>";
        html += '</div><div class="cus-quote-review__section-body">';
        html += "<p>" + _qrEsc(_qrPurpose) + "</p>";
        if (_qrPurpose === "Resale" && _qrEndUser) {
          html +=
            "<p><strong>" +
            (i.end_user_name || "End User Name") +
            ":</strong> " +
            _qrEsc(_qrEndUser) +
            "</p>";
        }
        html += "</div></div>";

        // Customer Info
        html += '<div class="cus-quote-review__section">';
        html += '<div class="cus-quote-review__section-header">';
        html +=
          '<h3 class="cus-quote-review__section-title">' +
          (i.rv_customer_info || "Customer Information") +
          "</h3>";
        html +=
          '<a href="/pages/quote-form" class="cus-quote-review__edit-link">' +
          (i.rv_edit || "Edit") +
          "</a>";
        html +=
          '</div><div class="cus-quote-review__section-body"><div class="cus-quote-review__info-grid">';
        var fields = [
          ["rv_name", "Name", _qrCustomer.name],
          ["rv_email", "Email", _qrCustomer.email],
          ["rv_phone", "Phone", _qrCustomer.phone],
          ["rv_company", "Company", _qrCustomer.company],
        ];
        fields.forEach(function (f) {
          html +=
            '<div class="cus-quote-review__info-item"><div class="cus-quote-review__info-label">' +
            (i[f[0]] || f[1]) +
            '</div><div class="cus-quote-review__info-value">' +
            _qrEsc(f[2]) +
            "</div></div>";
        });
        html += "</div></div></div>";

        // Addresses
        html += '<div class="cus-quote-review__section">';
        html += '<div class="cus-quote-review__section-header">';
        html +=
          '<h3 class="cus-quote-review__section-title">' +
          (i.rv_addresses || "Addresses") +
          "</h3>";
        html +=
          '<a href="/pages/quote-form" class="cus-quote-review__edit-link">' +
          (i.rv_edit || "Edit") +
          "</a>";
        html +=
          '</div><div class="cus-quote-review__section-body"><div class="cus-quote-review__info-grid">';
        html +=
          '<div class="cus-quote-review__info-item"><div class="cus-quote-review__info-label">' +
          (i.rv_shipping || "Shipping Address") +
          '</div><div class="cus-quote-review__info-value">' +
          _qrFormatAddr(_qrCustomer.shippingAddress) +
          "</div></div>";
        var billAddr =
          _qrCustomer.billSameAsShip !== false
            ? _qrCustomer.shippingAddress
            : _qrCustomer.billAddress;
        html +=
          '<div class="cus-quote-review__info-item"><div class="cus-quote-review__info-label">' +
          (i.rv_billing || "Billing Address") +
          '</div><div class="cus-quote-review__info-value">' +
          (_qrCustomer.billSameAsShip !== false
            ? "Same as shipping"
            : _qrFormatAddr(billAddr)) +
          "</div></div>";
        html += "</div></div></div>";

        // Attn & Remarks
        if (
          (_qrCustomer.attn && _qrCustomer.attn.length) ||
          _qrCustomer.remark
        ) {
          html += '<div class="cus-quote-review__section">';
          html += '<div class="cus-quote-review__section-header">';
          html +=
            '<h3 class="cus-quote-review__section-title">' +
            (i.rv_attn_remarks || "Attn & Remarks") +
            "</h3>";
          html +=
            '<a href="/pages/quote-form" class="cus-quote-review__edit-link">' +
            (i.rv_edit || "Edit") +
            "</a>";
          html += '</div><div class="cus-quote-review__section-body">';
          if (_qrCustomer.attn && _qrCustomer.attn.length) {
            html +=
              "<p><strong>" +
              (i.rv_attn || "Attn (CC Email)") +
              ":</strong> " +
              _qrCustomer.attn.map(_qrEsc).join(", ") +
              "</p>";
          }
          if (_qrCustomer.remark) {
            html +=
              "<p><strong>" +
              (i.rv_remark || "Remark") +
              ":</strong> " +
              _qrEsc(_qrCustomer.remark) +
              "</p>";
          }
          html += "</div></div>";
        }

        // Actions
        html += '<div class="cus-quote-review__actions">';
        html +=
          '<a href="/pages/quote-form" class="cus-quote-form__back-btn">' +
          (i.rv_back_to_form || "Back to Form") +
          "</a>";
        html +=
          '<button type="button" class="cus-quote-form__submit-btn" id="cus-qr-submit">' +
          (i.rv_submit || "Submit Quote") +
          "</button>";
        html += "</div>";

        if (_qrContentEl) _qrContentEl.innerHTML = html;

        // Bind submit
        var submitBtn = document.getElementById("cus-qr-submit");
        if (submitBtn) submitBtn.addEventListener("click", _qrHandleSubmit);
      }

      function _qrHandleSubmit() {
        var submitBtn = document.getElementById("cus-qr-submit");
        if (!submitBtn || submitBtn.disabled) return;

        submitBtn.disabled = true;
        submitBtn.textContent = _qrI18n.rv_submitting || "Submitting...";

        var billAddr =
          _qrCustomer.billSameAsShip !== false
            ? _qrCustomer.shippingAddress
            : _qrCustomer.billAddress;

        var quoteData = {
          products: _qrItems.map(function (item) {
            return {
              title: item.title || "",
              image: item.image || "",
              product_id: item.productId || item.product_id || "",
              variant_id: item.variantId || item.variant_id || "",
              variant_title: item.variantTitle || item.variant_title || "",
              sku: item.sku || "",
              price: (item.price || 0) / 100,
              quantity: item.quantity || 1,
              unitSum: ((item.price || 0) / 100) * (item.quantity || 1),
              hidePrice: item.hidePrice || false,
              physical: true,
              chargeTax: true,
              description: "",
              inventory: item.inventory || 0,
            };
          }),
          customer: {
            name: _qrCustomer.name || "",
            email: _qrCustomer.email || "",
            phone: _qrCustomer.phone || "",
            company: _qrCustomer.company || "",
            purposeOfPurchase: _qrPurpose,
            endUserName: _qrEndUser,
            customerId:
              (window.__quoteAuth && window.__quoteAuth.customerId) || "",
          },
          shippingAddress: _qrCustomer.shippingAddress || {},
          billAddress: billAddr || {},
          message: {
            attn: _qrCustomer.attn || [],
            remark: _qrCustomer.remark || "",
            comment: "",
          },
          otherInfo: {},
        };

        quoteApi
          .submitQuote(quoteData)
          .then(function (res) {
            if (_qrContentEl) _qrContentEl.style.display = "none";
            if (_qrSuccessEl) _qrSuccessEl.style.display = "";
            if (_qrQuoteIdEl)
              _qrQuoteIdEl.textContent = (res.data && res.data.quoteId) || "";

            try {
              sessionStorage.removeItem("cus_quote_items");
              sessionStorage.removeItem("cus_quote_customer");
              sessionStorage.removeItem("cus_quote_purpose");
              sessionStorage.removeItem("cus_quote_enduser");
            } catch (e) {}

            cusQuoteUI.updateCartBadge(0);
          })
          .catch(function (err) {
            submitBtn.disabled = false;
            submitBtn.textContent = _qrI18n.rv_submit || "Submit Quote";
            var msg =
              (err && err.error) || "Failed to submit quote. Please try again.";
            cusQuoteUI.showToast(msg, "error");
          });
      }

      _qrRender();
    }
  }

  /* ================================================================
   * Quote History — Account Page
   * Only activates when #cus-quote-history is present in the DOM.
   * ================================================================ */
  var _qhEl = document.getElementById("cus-quote-history");
  if (_qhEl) {
    var _qhLoading = document.getElementById("cus-qh-loading");
    var _qhEmpty = document.getElementById("cus-qh-empty");
    var _qhError = document.getElementById("cus-qh-error");
    var _qhContent = document.getElementById("cus-qh-content");
    var _qhTbody = document.getElementById("cus-qh-tbody");
    var _qhI18n = window.__quoteI18n || {};

    var _qhStatusMap = {
      "Client Initiated": { cls: "gray", label: "Client Initiated" },
      "EChem Confirming": { cls: "blue", label: "EChem Confirming" },
      "Client Reviewing": { cls: "orange", label: "Client Reviewing" },
      Converted: { cls: "green", label: "Converted" },
      Inactive: { cls: "red", label: "Inactive" },
    };

    function _qhEsc(str) {
      var d = document.createElement("div");
      d.textContent = str || "";
      return d.innerHTML;
    }

    function _qhFormatDate(dateStr) {
      if (!dateStr) return "";
      // createdAt is stored as Unix seconds — convert to ms
      var ts =
        typeof dateStr === "number" && dateStr < 1e12
          ? dateStr * 1000
          : dateStr;
      var d = new Date(ts);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }

    function _qhRender(quotes) {
      if (_qhLoading) _qhLoading.style.display = "none";

      if (!quotes || quotes.length === 0) {
        if (_qhEmpty) _qhEmpty.style.display = "";
        return;
      }

      var html = "";
      quotes.forEach(function (q) {
        var status = _qhStatusMap[q.status] || {
          cls: "gray",
          label: q.status || "Unknown",
        };
        html += "<tr>";
        html +=
          '<td><span class="cus-quote-id">' +
          _qhEsc(q.quoteId) +
          "</span></td>";
        html += "<td>" + _qhFormatDate(q.createdAt) + "</td>";
        html +=
          '<td><span class="cus-quote-history__badge cus-quote-history__badge--' +
          status.cls +
          '">' +
          _qhEsc(status.label) +
          "</span></td>";
        html += "</tr>";
      });

      if (_qhTbody) _qhTbody.innerHTML = html;
      if (_qhContent) _qhContent.style.display = "";
    }

    function _qhShowError() {
      if (_qhLoading) _qhLoading.style.display = "none";
      if (_qhError) _qhError.style.display = "";
    }

    // Only fetch if the customer is logged in
    if (window.__quoteAuth) {
      quoteApi
        .getHistory()
        .then(function (res) {
          _qhRender((res.data && res.data.quotes) || []);
        })
        .catch(function () {
          _qhShowError();
        });
    } else {
      if (_qhLoading) _qhLoading.style.display = "none";
      if (_qhEmpty) _qhEmpty.style.display = "";
    }
  }
})();
